import { useDidUpdateEffect } from '@app/core/hooks/useDidUpdateEffect';
import { showRibbonToast } from '@mi-front/ui';
import { useMemo, useRef } from 'react';
import { type Path, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import type { ReportDefinition, ReportDefinitionData } from '../../types';

interface UseSegmentationEditorProps {
  sectionId: string;
}

export function useSegmentationEditor({ sectionId }: UseSegmentationEditorProps) {
  const intl = useIntl();
  const { watch, setValue } = useFormContext<ReportDefinition>();
  const data = watch('data');
  const sections = (data?.sections || []) as ReportDefinitionData['sections'];
  const sectionIndex = sections.findIndex((s) => s.id === sectionId);

  const definitionSection = sectionIndex !== -1 ? sections[sectionIndex] : undefined;
  const segmentation = definitionSection?.segmentation;

  // Get columns for cleanup logic
  const columns = definitionSection?.columns || [];
  const selectedColumnIds = useMemo(() => new Set(columns.map((col) => col.id)), [columns]);

  // Ref to track if we have already notified about the removal to avoid double toasts
  const lastRemovedColumnIdRef = useRef<string | null>(null);

  // Clean up orphaned segmentation (referencing a column that is no longer selected)
  useDidUpdateEffect(() => {
    if (sectionIndex === -1 || !segmentation) return;

    if (!selectedColumnIds.has(segmentation.columnId)) {
      // Only notify if we haven't notified for this specific column ID
      if (lastRemovedColumnIdRef.current !== segmentation.columnId) {
        showRibbonToast({
          severity: 'info',
          title: intl.formatMessage({
            id: 'audit.reportDefinitions.segmentation.removedNotification',
            defaultMessage:
              'La segmentación se ha deshabilitado porque la columna asociada fue removida.',
          }),
        });
        lastRemovedColumnIdRef.current = segmentation.columnId;
      }

      setValue(`data.sections.${sectionIndex}.segmentation` as Path<ReportDefinition>, undefined, {
        shouldDirty: true,
      });
    } else {
      // Reset ref if the column is present
      if (lastRemovedColumnIdRef.current === segmentation.columnId) {
        lastRemovedColumnIdRef.current = null;
      }
    }
  }, [selectedColumnIds, sectionIndex, segmentation, setValue, intl]);

  const handleSetSegmentation = (columnId: string) => {
    if (sectionIndex === -1) return;
    setValue(
      `data.sections.${sectionIndex}.segmentation` as Path<ReportDefinition>,
      { columnId, hideColumn: false },
      { shouldDirty: true },
    );
  };

  const handleClearSegmentation = () => {
    if (sectionIndex === -1) return;
    setValue(`data.sections.${sectionIndex}.segmentation` as Path<ReportDefinition>, undefined, {
      shouldDirty: true,
    });
  };

  const handleToggleHideColumn = (hide: boolean) => {
    if (sectionIndex === -1 || !segmentation) return;
    setValue(
      `data.sections.${sectionIndex}.segmentation.hideColumn` as Path<ReportDefinition>,
      hide,
      { shouldDirty: true },
    );
  };

  return {
    sectionIndex,
    definitionSection,
    segmentation,
    handleSetSegmentation,
    handleClearSegmentation,
    handleToggleHideColumn,
  };
}
